import { Injectable, NgZone } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/auth";
import { AngularFireDatabase } from "@angular/fire/database";
import { Router } from "@angular/router";
import { first } from "rxjs/operators";
import { Observable } from "rxjs";
import { tap, map, take, switchMap } from "rxjs/operators";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  isLogged: boolean;
  user$: Observable<any>;
  private readonly TOKEN_REFRESH_INTERVAL_MS = 55 * 60 * 1000; // 55 minutes (before 1 hour expiration)
  private tokenRefreshInterval: any;

  constructor(
    private httpClient: HttpClient,
    private fireAuth: AngularFireAuth,
    private firebase: AngularFireDatabase,
    private router: Router,
    private ngZone: NgZone,
  ) {
    this.user$ = this.fireAuth.authState;

    this.authStatusListener();
  }

  SignUp(sysuser) {
    const session = this.GetCurrentSession();

    return this.httpClient
      .post<any>(environment.host + "/api/user/create/", {
        session,
        name: sysuser.name,
        email: sysuser.email,
        role: sysuser.role,
      })
      .toPromise()
      .then((res) => {
        if (!res || !res.success) {
          window.alert(
            "Could not create user: " + ((res && res.error) || "unknown error"),
          );
        }
        return res;
      })
      .catch((error) => {
        window.alert(
          "Could not create user: " +
            ((error && error.message) || "request failed"),
        );
      });
  }

  SignIn(email, password) {
    return this.fireAuth
      .signInWithEmailAndPassword(email, password)
      .then((credential) => {
        this.SetCurrentUser(credential.user.uid);

        credential.user.getIdToken(true).then((jwtoken) => {
          localStorage.setItem("session", jwtoken);
        });

        this.fireAuth.onIdTokenChanged(async (user) => {
          credential.user.getIdToken(true).then((jwtoken) => {
            localStorage.setItem("session", jwtoken);
          });
        });

        this.startTokenRefresh();
        this.InitializeSystemUser(credential.user.uid);
      })
      .catch((error) => {
        if (
          error.code == "auth/wrong-password" ||
          error.code == "auth/invalid-email"
        )
          window.alert(
            "The username or password is incorrect. \n Tip: Please make sure you did not copy any spaces",
          );
        else window.alert(error.message);
      });
  }

  SignOut() {
    this.clearTokenRefresh();
    return this.fireAuth.signOut().then(() => {
      localStorage.removeItem("user");
      localStorage.removeItem("userMetaData");
      localStorage.removeItem("session");
      this.router.navigate(["login"]);
    });
  }

  async SendPasswordResetEmail(passwordResetEmail) {
    await this.fireAuth
      .sendPasswordResetEmail(passwordResetEmail)
      .then(() => {
        window.alert("Password reset email sent, check your inbox.");
      })
      .catch((error) => {
        window.alert(error);
      });
  }

  ConfirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    return this.fireAuth.confirmPasswordReset(oobCode, newPassword);
  }

  ApplyActionCode(oobCode: string): Promise<void> {
    return this.fireAuth.applyActionCode(oobCode);
  }

  GetCurrentUser(): string {
    return localStorage.getItem("user");
  }

  SetCurrentUser(user: string) {
    localStorage.setItem("user", user);
  }

  SetCurrentUserMetaData(metadata: any) {
    localStorage.setItem("userMetaData", metadata);
  }

  GetCurrentSession(): string {
    return localStorage.getItem("session");
  }

  GetCurrentUserMetaData(): any {
    return JSON.parse(localStorage.getItem("userMetaData"));
  }

  isAuthenticated(): Observable<boolean> {
    return this.user$.pipe(
      take(1),
      map((authState) => !!authState),
      tap((isLogged) => {
        if (isLogged) {
          if (!this.GetCurrentUser()) {
            this.SignOut();
          }

          let sessionId = this.GetCurrentSession();

          if (sessionId) {
            const params = new HttpParams().set("session", sessionId);

            this.httpClient
              .get<any>(environment.host + "/session/", { params })
              .subscribe((valid) => {
                if (!valid) {
                  this.SignOut();
                }
              });
          }
        }
        if (!isLogged) {
        }
      }),
    );
  }

  InitializeSystemUser(user: string) {
    this.firebase
      .object(`/Users/${user}/`)
      .valueChanges()
      .subscribe((user: any) => {
        if (user) {
          this.SetCurrentUserMetaData(JSON.stringify(user.info));

          this.ngZone.run(() => {
            this.router.navigate(["surveys"]);
          });
        }
      });
  }

  authStatusListener() {
    this.fireAuth.onAuthStateChanged((credential) => {
      if (credential) {
        this.startTokenRefresh();
      } else {
        this.clearTokenRefresh();
        // Redirect to login if not already there. Don't bounce users off
        // public, logged-out pages: the login page itself, or the email-action
        // handler (/auth/action) for password reset / email verification —
        // those flows run while signed out and must not be interrupted.
        const path = window.location.pathname;
        const onPublicPage =
          this.router.url === "/login" || path.indexOf("/auth/action") === 0;
        if (!onPublicPage) {
          this.router.navigate(["login"]);
        }
      }
    });
  }

  NavigateToURL(url: string) {
    this.router.navigate([url]);
  }

  private startTokenRefresh() {
    // Set new interval to refresh token every 55 minutes (before 1 hour expiration)
    this.tokenRefreshInterval = setInterval(() => {
      this.fireAuth.currentUser.then((user) => {
        if (user) {
          user
            .getIdToken(true)
            .then((jwtoken) => {
              localStorage.setItem("session", jwtoken);
            })
            .catch((error) => {
              // If token refresh fails, sign out the user
              this.SignOut();
            });
        }
      });
    }, this.TOKEN_REFRESH_INTERVAL_MS);
  }

  private clearTokenRefresh() {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
  }
}
